/**
 * The non-component options of the /design routes (P0-9 deliverable 14). This
 * module imports no component and no style sheet, so the route files can load
 * it eagerly while TanStack's code splitting keeps the page itself (and
 * design.css) in the route's own chunk.
 *
 * - `guardDesignSession` (the layout route's `beforeLoad`, stage 2): P0-2's
 *   `requireSession(location.pathname)`. Signed out (a 401), the browser goes
 *   to `/login?next=<path>` and the route never renders; any other failure
 *   rejects, and the route shows P0-2's `SessionError`. It puts `me` in the
 *   route context, so the guard's call is the only `auth.me` request. On
 *   entering /design it waits for the answer; on a navigation inside
 *   /design (TanStack's cause `stay`) it answers with the session it already
 *   checked and checks again without holding the navigation (the switcher's
 *   radios are controlled by the URL and would otherwise lag a round trip).
 * - `loadDesignFlag` (the loader): the routes render only when
 *   `flags.design` in that `me` is true, read with `flagsFromMe(me)`;
 *   otherwise the app's not-found page shows. Never a build-time constant:
 *   the GM views /design on the production build. Without a `me` (no guard
 *   ran) or with a malformed one it fails closed.
 * - `validateDesignSearch`: the switcher's URL state, invalid values dropped.
 * - `checkScreenParam`: an unknown screen slug is not found: the app's
 *   not-found page replaces the whole page, as it does when the flag is off.
 *   A known one goes into the route context as a `ScreenSlug`, which is what
 *   the screen component reads, so no other slug can reach it.
 */
import { flagsFromMe, type MeResponse } from '@codex/contracts';
import { notFound, rootRouteId } from '@tanstack/react-router';
import { requireSession } from '../auth/require-session.js';
import { parseDesignSearch, toUrlSearch } from './lib/design-search.js';
import { isScreenSlug, type ScreenSlug } from './lib/slugs.js';

/** The last session the guard saw answered, for navigations inside /design. */
let checked: MeResponse | undefined;

export async function guardDesignSession({
  location,
  cause,
}: {
  location: { pathname: string };
  cause: 'enter' | 'stay' | 'preload';
}): Promise<{ me: MeResponse }> {
  if (cause === 'stay' && checked) {
    // Already inside /design with a checked session (a switcher change or
    // another screen): the navigation does not wait for the network, since
    // the page's content is already in the browser, but the session is checked
    // again, so one that ended during the visit still goes to sign in. Any
    // other failure leaves the page as it is.
    requireSession(location.pathname).then(
      (me) => {
        checked = me;
      },
      () => undefined,
    );
    return { me: checked };
  }
  const me = await requireSession(location.pathname);
  checked = me;
  return { me };
}

export function loadDesignFlag({ context }: { context: { me?: MeResponse } }): void {
  let design = false;
  try {
    if (context.me) design = flagsFromMe(context.me).design;
  } catch {
    design = false;
  }
  if (!design) throw notFound();
}

export function validateDesignSearch(
  raw: Record<string, unknown>,
): Record<string, string | undefined> {
  return toUrlSearch(parseDesignSearch(raw));
}

export function checkScreenParam({ params }: { params: { screen: string } }): {
  screen: ScreenSlug;
} {
  if (!isScreenSlug(params.screen)) throw notFound({ routeId: rootRouteId });
  return { screen: params.screen };
}
